import { useQuery } from '@apollo/client';
import { InputLabel, Box } from '@mui/material';
import React from 'react';
import { Suggest } from '../../../../components/Suggest';
import {
  BrandKey,
  CategoryStatus,
  GetArticleQuery_query_article_Article_categories_ArticleCategory,
  GetBrandCategoriesQuery,
  GetBrandCategoriesQueryVariables,
  GetBrandFilterCategoriesQuery,
  GetBrandFilterCategoriesQueryVariables,
} from '../../../../__generated__/queries-web';
import {
  GET_BRAND_CATEGORIES,
  GET_BRAND_FILTER_CATEGORIES,
} from '../../../SlideshowEdit/CategorySelector/getBrandCategories.web.graphql';
import { actions, ArticleEditAction } from '../../reducer';

interface CategoriesInputsProps {
  brandKey: BrandKey;
  categories: GetArticleQuery_query_article_Article_categories_ArticleCategory[];
  dispatch: React.Dispatch<ArticleEditAction>;
  multiple?: boolean;
}

// Utility function to filter out hidden categories and their children
const filterHiddenCategories = <
  T extends { metas?: { hidden?: boolean }; children?: any[] }
>(
  categories: T[],
): T[] => {
  return categories
    .filter((category) => category.metas?.hidden !== true)
    .map((category) => ({
      ...category,
      children:
        category.children && category.children.length > 0
          ? filterHiddenCategories(category.children)
          : category.children,
    }));
};

export const CategoriesInput: React.FC<CategoriesInputsProps> = ({
  brandKey,
  categories,
  dispatch,
  multiple,
}) => {
  const isFAC = brandKey === BrandKey.FAC;
  const { data } = useQuery<
    GetBrandCategoriesQuery | GetBrandFilterCategoriesQuery,
    GetBrandCategoriesQueryVariables | GetBrandFilterCategoriesQueryVariables
  >(isFAC ? GET_BRAND_FILTER_CATEGORIES : GET_BRAND_CATEGORIES, {
    variables: {
      brandKey,
      status: CategoryStatus.Published,
      title_not_in: isFAC ? ['Recettes'] : undefined,
    },
  });

  // Filter out hidden categories and their children
  const filteredCategories = data?.categories
    ? filterHiddenCategories(data.categories)
    : [];

  return (
    <Box sx={{ width: '100%' }}>
      <InputLabel shrink>Catégorie</InputLabel>
      <Suggest
        selectedItems={categories.map(({ category }) => category)}
        placeholder="Rechercher une catégorie"
        suggestions={filteredCategories?.[0]?.children || []}
        multiple={multiple}
        maxNumberSuggest={2}
        isPrefetched
        onChange={(items) => {
          dispatch(
            actions.updateField({
              field: 'categories',
              value: items.map(({ id, title }) => ({
                category: { id, title },
              })),
            }),
          );
        }}
      />
    </Box>
  );
};
